import { useEffect, useMemo } from "react";
import { useParticipants, useRoomContext } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { PARTICIPANT_ROLES } from "@circle-react/helpers/liveStreamRoomHelpers";
import type { ProviderRoom, ProviderRoomOptions } from "../types";
import { useRoom } from "../useRoom";
import { useLivekitErrorHandler } from "./useLivekitErrorHandler";
import { useLivekitLocalMetadata } from "./useLivekitLocalMetadata";

export const useLivekitRoom = ({
  token,
  onJoinError,
  isStreamEnded,
}: ProviderRoomOptions): ProviderRoom => {
  const circleRoom = useRoom();
  const url = circleRoom?.provider_url ?? "";
  const room = useRoomContext();
  const participants = useParticipants();
  const { metadata: localMetadata } = useLivekitLocalMetadata();

  useLivekitErrorHandler({ onDisconnect: onJoinError });

  useEffect(() => {
    const join = async () => {
      if (
        !!token &&
        !isStreamEnded &&
        room.state === ConnectionState.Disconnected
      ) {
        try {
          await room.connect(url, token);
        } catch (e: any) {
          console.error("Could not connect to room: ", e.message);
          onJoinError && onJoinError();
        }
      }
    };
    void join();

    return () => {
      if (room.state === ConnectionState.Connected) {
        void room.disconnect();
      }
    };
  }, [token, room, isStreamEnded]);

  const isSomeoneScreenSharing = useMemo(
    () => participants.some(participant => participant.isScreenShareEnabled),
    [participants],
  );

  return {
    isConnected: room.state === ConnectionState.Connected,
    isJoining: room.state === ConnectionState.Connecting,
    isHost: localMetadata?.role === PARTICIPANT_ROLES.HOST,
    isHlsSpectator: localMetadata?.role === PARTICIPANT_ROLES.HLS_SPECTATOR,
    role: localMetadata?.role,
    isSomeoneScreenSharing,
  };
};
